import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

// ─── Müllerian Anomalies ────────────────────────────────────────────

const mullerian: ContentBlock<"condition"> = {
  id: makeId("dx-mullerian-anomalies"),
  kind: "condition",
  tags: ["her", "evidence-based"],
  title: "Uterine anomalies",
  subtitle: "Congenital differences in uterine shape that may affect pregnancy",
  body: {
    conditionName: "Müllerian anomalies (uterine anomalies)",
    category: "structural",
    description:
      "Müllerian anomalies are congenital differences in the shape or structure of the uterus, fallopian tubes, or upper vagina, resulting from incomplete development or fusion of the Müllerian ducts during embryonic development. They range from a minor uterine septum to a completely duplicated or absent uterus. Most are discovered incidentally during fertility workup.",
    symptoms: [
      "Often no symptoms — discovered during fertility evaluation",
      "Recurrent pregnancy loss (especially second trimester)",
      "Preterm delivery",
      "Breech presentation",
      "Painful periods in some types (e.g., obstructed hemivagina)",
    ],
    howDiagnosed:
      "Initial suspicion on HSG or saline sonogram showing abnormal cavity shape. 3D ultrasound is excellent for classification. MRI is the gold standard for definitive diagnosis and detailed characterization. Hysteroscopy can confirm and treat certain anomalies (septum).",
    impactOnFertility:
      "Impact varies by type. A uterine septum (most common, ~35% of anomalies) is associated with the highest pregnancy loss rate (up to 60%) but is surgically correctable. Unicornuate and bicornuate uteri increase preterm birth risk but many women conceive successfully. A complete uterine absence (MRKH) requires a gestational carrier.",
    prevalence:
      "Present in approximately 5-7% of the general population and up to 16% of women with recurrent pregnancy loss.",
    treatmentOptions: [
      "Hysteroscopic septum resection (for septate uterus — most common and treatable)",
      "Cervical cerclage for incompetent cervix associated with certain anomalies",
      "Close obstetric monitoring for preterm birth risk",
      "IVF may be needed if tubes are affected",
      "Gestational carrier for uterine absence or non-functional uterus",
    ],
    prognosis:
      "For septate uterus, surgical correction dramatically improves outcomes (pregnancy rates improve from ~40% to 75-85%). Unicornuate and bicornuate uteri have reasonable pregnancy rates with appropriate monitoring. Outcomes are highly dependent on the specific type and severity.",
    forWhom: "her",
  },

  sources: [
    {
      title:
        "The ESHRE/ESGE consensus on the classification of female genital tract congenital anomalies",
      authors: "Grimbizis GF, Gordts S, Di Spiezio Sardo A, et al.",
      journal: "Human Reproduction",
      year: 2013,
      url: "https://academic.oup.com/humrep/article/28/8/2032/640370",
      evidenceLevel: "guideline",
    },
    {
      title: "Uterine septum: a guideline",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2016,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(mullerian);

// ─── Thin Endometrium ───────────────────────────────────────────────

const thinEndo: ContentBlock<"condition"> = {
  id: makeId("dx-thin-endometrium"),
  kind: "condition",
  tags: ["her", "ivf", "fet", "evidence-based"],
  title: "Thin endometrium",
  subtitle: "Uterine lining too thin for optimal embryo implantation",
  body: {
    conditionName: "Thin endometrium",
    category: "structural",
    description:
      "Thin endometrium refers to a uterine lining that fails to reach adequate thickness (typically < 7 mm) for embryo implantation despite appropriate hormonal preparation. A healthy endometrium of 7-14 mm with a trilaminar (three-layer) pattern provides the best environment for implantation. Persistent thin lining is a challenging condition that can be a barrier to successful embryo transfer.",
    symptoms: [
      "Light menstrual periods",
      "Recurrent implantation failure despite good embryos",
      "Thin lining on monitoring ultrasounds during FET preparation",
      "Often no symptoms — found during cycle monitoring",
    ],
    howDiagnosed:
      "Measured on transvaginal ultrasound during the peri-implantation window. Considered thin if < 7 mm despite adequate estrogen levels. Pattern (trilaminar vs. homogeneous) is also assessed. Blood flow to the endometrium may be evaluated with Doppler ultrasound. Hysteroscopy can rule out adhesions or other cavity pathology.",
    impactOnFertility:
      "Implantation rates decline significantly with endometrial thickness below 7 mm. Studies show pregnancy rates drop by ~25% per mm below 8 mm. However, pregnancies do occur with thinner linings — the threshold is not absolute. Pattern and blood flow may be as important as thickness.",
    prevalence:
      "Affects approximately 5-12% of women undergoing IVF/FET cycles.",
    treatmentOptions: [
      "Extended estrogen supplementation (higher doses or longer duration)",
      "Vaginal estrogen (Estrace) added to oral or patch regimen",
      "Low-dose aspirin to improve blood flow",
      "Viagra (sildenafil) vaginal suppositories (off-label, improves uterine blood flow)",
      "Pentoxifylline and vitamin E combination",
      "G-CSF (granulocyte colony-stimulating factor) intrauterine infusion",
      "PRP (platelet-rich plasma) intrauterine infusion",
      "Hysteroscopy to rule out and treat adhesions",
    ],
    prognosis:
      "Many cases of thin endometrium improve with adjusted protocols. When the lining reaches ≥ 7 mm with a trilaminar pattern, outcomes are comparable to normal-thickness cycles. Refractory cases (lining stays < 6 mm despite all interventions) may benefit from gestational carrier consideration.",
    forWhom: "her",
  },

  sources: [
    {
      title: "Good practice recommendations on recurrent implantation failure",
      authors: "ESHRE Working Group on RIF (Cimadomo D et al.)",
      journal: "Human Reproduction Open",
      year: 2023,
      url: "https://pubmed.ncbi.nlm.nih.gov/37325540/",
      evidenceLevel: "guideline",
    },
    {
      title:
        "Endometrial thickness and pregnancy rates after IVF: a systematic review and meta-analysis",
      authors: "Kasius A, Smit JG, Torrance HL, et al.",
      journal: "Human Reproduction Update",
      year: 2014,
      url: "https://academic.oup.com/humupd/article/20/4/530/2952657",
      evidenceLevel: "meta-analysis",
    },
  ],
};

registerBlock(thinEndo);
